import React, { useState, useEffect, useRef } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { API_ENDPOINT } from "../../../../../constants/constants";

export default function StateSelector({
  countryCode,
  selectedState,
  onChange,
  required,
}) {
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    const fetchStates = async () => {
      if (!countryCode) {
        setStates([]);
        return;
      }

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoading(true);
      try {
        const response = await fetch(
          `${API_ENDPOINT}visitor/get-states-of-country/${countryCode}`,
          { signal: controller.signal }
        );
        const data = await response.json();

        const stateData = data.map((state) => ({
          name: state.name,
          code: state.isoCode,
        }));

        // Only update if still relevant
        if (!controller.signal.aborted) {
          setStates(stateData);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching states:", error);
          setStates([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchStates();

    return () => {
      // Cleanup pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [countryCode]);

  const handleChange = (event, value) => {
    onChange?.(value);
  };

  return (
    <Autocomplete
      size="small"
      options={states}
      value={selectedState || null}
      onChange={handleChange}
      getOptionLabel={(option) => (option ? option.name : "")}
      isOptionEqualToValue={(option, value) => option.code === value.code}
      loading={loading}
      disabled={!countryCode || loading}
      noOptionsText={
        countryCode ? "No states available" : "Select country first"
      }
      renderInput={(params) => (
        <TextField
          required={required}
          {...params}
          label="Select State"
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
